import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';


import { NAV_GROUPS } from '../navigation';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import {
  isNarrowViewport,
  readStoredSidebarCollapsed,
  sidebarIsCollapsed,
  writeStoredSidebarCollapsed,
  MAIN_CONTENT_ID,
  SIDEBAR_ID,
  type BreadcrumbItem,
  type NavIcon,
  type NavItem,
} from './shellState';
import { InShellContext, PageActionsContext } from './pageActionsContext';
import styles from './AppShell.module.css';

/**
 * The application shell: the three landmarks, the skip link, and the frame every
 * screen renders inside.
 *
 * **Landmarks.** `banner` (the topbar), `navigation` (twice — the sidebar and
 * the breadcrumb, each with its own `aria-label`, because two unnamed navs are
 * indistinguishable in a landmark list) and `main`. The plan's own sample test
 * does `getByRole('navigation')`, which throws the moment the breadcrumb the
 * same plan specifies exists; see the report's plan-defect list.
 *
 * **The skip link moves focus, it does not merely scroll.** `href="#…"` alone
 * scrolls the target into view and leaves the caret where it was in several
 * engines, so the very next Tab goes back into the nav the link just skipped.
 * `<main tabindex="-1">` plus an explicit `focus()` is what makes the link do
 * what it says. `preventScroll` is deliberately NOT used: the scroll is the
 * visible half of the affordance.
 *
 * **The shell creates no portal.** `overlayRoot.ts` (task 23) is the single
 * portal host and owns the modal stack, the scroll lock and the background
 * `aria-hidden`. A second host would break the background-hiding, because
 * hiding "every child of body except the overlay root" only works if there is
 * exactly one overlay root. Nothing here portals; the topbar's switch popovers
 * are ordinary absolutely-positioned children, which is also what makes them
 * correctly disappear behind a modal.
 *
 * **Heading order.** The shell owns exactly one `<h1>` — the page title — and
 * screens render `<h2>` downwards inside `children`. The brand lockup is
 * deliberately not a heading: a brand in the heading list puts every screen
 * under the same name.
 */

export interface AppShellProps {
  navItems: readonly NavItem[];
  /** The path that is current. Drives `aria-current="page"` on exactly one item. */
  activePath: string;
  /** The page title, rendered as the shell's single `<h1>`. */
  title: string;
  /**
   * The current page's nav icon, shown in the title row's chip. Optional: a
   * screen with no nav entry (a forced password change) has none.
   */
  titleIcon?: NavIcon;
  breadcrumb?: readonly BreadcrumbItem[];
  notificationCount?: number;
  onNavigate?: (path: string) => void;
  onNotificationsClick?: () => void;
  /** Passed straight to the topbar's account menu; see `TopbarProps`. */
  onSignOut?: () => void;
  children?: ReactNode;
}

export function AppShell({
  navItems,
  activePath,
  title,
  titleIcon: TitleIcon,
  breadcrumb,
  notificationCount,
  onNavigate,
  onNotificationsClick,
  onSignOut,
  children,
}: AppShellProps) {
  const { t } = useTranslation();
  const mainRef = useRef<HTMLElement>(null);
  // The title row's action slot. State, not a ref: `PageActions` must
  // re-render once the element exists so it can portal into it.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);

  // Handoff, Responsive: "Below 1100px the sidebar collapses to icons." Read in
  // the initialiser, not from an effect, so the first frame is already right.
  const [narrow, setNarrow] = useState(() => isNarrowViewport());

  // `null` = never chosen. See shellState.ts: the viewport supplies the default,
  // an explicit choice outranks it.
  const [preference, setPreference] = useState<boolean | null>(readStoredSidebarCollapsed);
  const lastPersisted = useRef(preference);

  useEffect(() => {
    const onResize = () => {
      setNarrow(isNarrowViewport());
    };
    window.addEventListener('resize', onResize);
    // The viewport can change between the initialiser and this effect (a
    // devtools dock, a restored window), so re-read rather than trust the render.
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    // Persist on CHANGE, never on mount — task 18 §4. Merely opening the app
    // must not pin the account to today's default. The ref tracks what was
    // last WRITTEN rather than what was restored, so toggling back to the
    // restored value still reaches storage.
    if (preference === null || preference === lastPersisted.current) return;
    lastPersisted.current = preference;
    writeStoredSidebarCollapsed(preference);
  }, [preference]);

  const collapsed = sidebarIsCollapsed(preference, narrow);

  const toggleSidebar = useCallback(() => {
    setPreference((current) => !sidebarIsCollapsed(current, isNarrowViewport()));
  }, []);

  /*
   * THE FOOTER IS A GROUP, NOT A LIST OF NAMES. `account` is the one group id
   * `NAV_GROUPS` does not render as a section (see its own docblock), so
   * splitting on it is what keeps this file from growing a second hard-coded
   * copy of "which entries are chrome" that the nav table could drift from.
   *
   * `settings` never reaches the sidebar at all — `App.tsx` filters it out
   * upstream, because the topbar's account menu already carries it and one
   * destination in two places is a nav that says the same thing twice — so in
   * practice this is Support, alone at the foot of the rail.
   */
  const sectionItems = navItems.filter((item) => item.group !== 'account');
  const footerItems = navItems.filter((item) => item.group === 'account');

  function skipToContent(event: MouseEvent<HTMLAnchorElement>) {
    const main = mainRef.current;
    if (main === null) return;
    // `preventDefault` keeps the fragment out of the URL; `focus()` is what
    // actually does the skipping, and focusing an off-screen element scrolls it
    // into view for free.
    event.preventDefault();
    main.focus();
  }

  return (
    <div className={styles['shell']} data-testid="shell">
      {/*
        First focusable thing in the document, by DOM order rather than by
        `tabindex` — a positive tabindex would reorder the whole page.
      */}
      <a href={`#${MAIN_CONTENT_ID}`} className={styles['skipLink']} onClick={skipToContent}>
        {t('a11y.skipToContent')}
      </a>

      <Sidebar
        id={SIDEBAR_ID}
        items={sectionItems}
        groups={NAV_GROUPS}
        footerItems={footerItems}
        activePath={activePath}
        collapsed={collapsed}
        onNavigate={onNavigate}
        className={styles['sidebarArea']}
      />

      <Topbar
        className={styles['topbarArea']}
        breadcrumb={breadcrumb}
        notificationCount={notificationCount}
        sidebarCollapsed={collapsed}
        onToggleSidebar={toggleSidebar}
        sidebarId={SIDEBAR_ID}
        onNavigate={onNavigate}
        onNotificationsClick={onNotificationsClick}
        onSignOut={onSignOut}
      />

      <main id={MAIN_CONTENT_ID} ref={mainRef} tabIndex={-1} className={styles['main']}>
        {/* Full width — see `.content` in AppShell.module.css. */}
        <div className={styles['content']} data-testid="content">
          <div className={styles['titleRow']}>
            {TitleIcon === undefined ? null : (
              <span className={styles['titleIcon']} aria-hidden="true">
                <TitleIcon weight="duotone" />
              </span>
            )}
            <h1 className={styles['title']}>{title}</h1>
            <div ref={setActionsSlot} className={styles['titleActions']} data-testid="pageActions" />
          </div>
          <InShellContext.Provider value>
            <PageActionsContext.Provider value={actionsSlot}>{children}</PageActionsContext.Provider>
          </InShellContext.Provider>
        </div>
      </main>
    </div>
  );
}
